/**
 * Search across thesauruses (Thema, Emner, GREP-codes or Sjanger/form)
 * and update source fields with selected thesaurus codes.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {assert} from 'assert-ts';
import type {ThesaurusAction} from 'types';
import type {
  MultiThesaurusValue,
  ThesaurusInfo,
} from 'sceneExtensions/extensions/multiThesaurusEdit/types';
import type {SetSourceValue} from 'sceneExtensions/types';
import type {ThesaurusId, ThesaurusMap} from 'services/thesaurus/types';
import {useThesaurusMap, useTrimmedThesaurusMap} from 'services/thesaurus';
import type {SelectAction} from '../../editThesaurus/types';
import type {MultiEditValue, MultiSearchResult, ShowCodesMap} from '../types';
import {
  addNodeIdAndDuplicateIdsToEditValue,
  getSelectAction,
  removeNodeIdFromThesaurusValue,
} from '../../editThesaurus/functions';
import {
  getMultiThesaurusMaps,
  mapMultiNodeIdsToThesaurusValue,
  mapMultiThesaurusValueToNodeIds,
  searchMultiThesaurus,
} from '../functions';

let timestamp = 0;

export type MultiThesaurusEditStateContextType = {
  thesaurusIds: ThesaurusId[];
  showCodes: ShowCodesMap;
  thesauruses: ThesaurusMap;
  searchQuery?: string;
  setSearchQuery: (value: string) => void;

  /**
   * When searchQuery is empty/undefined (e.g. not yet searched), searchResult is undefined.
   * Otherwise, result will contain search results for each thesaurus.
   */
  searchResult?: MultiSearchResult;

  getNodeAction: (
    thesaurusId: ThesaurusId,
    nodeId: string,
  ) => SelectAction | undefined;

  /**
   * Adds node, removes any nodes in conflict with the added node, i.e.
   * ancestor or decendant nodes. Also, if thesaurus contains duplicate
   * codes, ids of all duplicates will be added, and all nodes in
   * conflict removed for each duplicate.
   */
  addNodeId: (thesaurusId: ThesaurusId, nodeId: string) => void;
  removeNodeId: (thesaurusId: ThesaurusId, nodeId: string) => void;

  close: () => void;
};

export const MultiThesaurusEditStateContext = createContext<
  MultiThesaurusEditStateContextType | undefined
>(undefined);

export const useMultiThesaurusEditStateContextProviderValue = (
  thesaurusesInfo: ThesaurusInfo[] | undefined = [],
  /**
   * Selected codes, if any, pr thesaurusId. If thesaurus contains duplicate codes,
   * i.e. nodes with different id but same code, only one
   * of each code should be given
   */
  thesaurusValue: MultiThesaurusValue,
  /**
   * If thesaurus contains duplicate codes, only one code will be
   * set for a code with duplicates
   */
  onSetSourceThesaurusValue: SetSourceValue<
    MultiThesaurusValue,
    ThesaurusAction
  >,
  onClose: () => void,
): MultiThesaurusEditStateContextType => {
  const {thesaurusIds, showCodes, emptySearchResult} = useMemo(() => {
    const ids = thesaurusesInfo.map(({thesaurusId}) => thesaurusId) ?? [];
    const showCodes = thesaurusesInfo.reduce<ShowCodesMap>(
      (acc, {thesaurusId, showCode}) => ({
        ...acc,
        [thesaurusId]: showCode ?? false,
      }),
      {},
    );

    const emptyResult = ids.reduce<MultiSearchResult>(
      (acc, id) => ({
        ...acc,
        [id]: [],
      }),
      {},
    );
    return {
      thesaurusIds: ids,
      showCodes,
      emptySearchResult: emptyResult,
    };
  }, [thesaurusesInfo]);
  const fullThesauruses = useThesaurusMap(thesaurusIds);
  const thesauruses = useTrimmedThesaurusMap(fullThesauruses);

  const mapsRef = useRef(getMultiThesaurusMaps(thesauruses));

  // Keep track of timestamp of source value (thesaurusValue) and
  // edit value to avoid cyclic updates, i.e. don't update
  // source value when source value's timestamp is newer than
  // edit value's timestamp
  const multiEditValueTimestampRef = useRef(timestamp++);
  const thesaurusValueTimestampRef = useRef(timestamp++);

  /**
   * Node ids currently being edited, mapped from thesaurus value (codes)
   */
  const [multiEditValue, setMultiEditValue] = useState<MultiEditValue>(
    mapMultiThesaurusValueToNodeIds(
      thesaurusIds,
      thesaurusValue,
      mapsRef.current,
    ),
  );
  const [searchQuery, setSearchQuery] = useState<string>();
  const searchQueryRef = useRef<typeof searchQuery>(searchQuery);

  const [searchResult, setSearchResult] = useState<
    MultiSearchResult | undefined
  >();

  const handleSetSourceThesaurusValue = useCallback(
    (multiValue: MultiEditValue) => {
      onSetSourceThesaurusValue(
        mapMultiNodeIdsToThesaurusValue(
          thesaurusIds,
          multiValue,
          mapsRef.current,
        ),
        'set',
        'keepOpen',
      );
    },
    [onSetSourceThesaurusValue, thesaurusIds],
  );

  const handleAddNodeId = useCallback(
    (thesaurusId: ThesaurusId, nodeId: string) => {
      setMultiEditValue(old => {
        const singleValue = addNodeIdAndDuplicateIdsToEditValue(
          nodeId,
          old[thesaurusId] ?? [],
          thesauruses[thesaurusId]!,
          mapsRef.current[thesaurusId]!.duplicateIdToIds,
        );

        return {
          ...old,
          [thesaurusId]: singleValue,
        };
      });

      multiEditValueTimestampRef.current = timestamp++;
    },
    [thesauruses],
  );

  const handleRemoveNodeId = useCallback(
    (thesaurusId: ThesaurusId, nodeId: string) => {
      setMultiEditValue(old => {
        let singleValue = removeNodeIdFromThesaurusValue(
          nodeId,
          old[thesaurusId] ?? [],
        );
        mapsRef.current[thesaurusId]!.duplicateIdToIds.get(nodeId)?.forEach(
          duplicateId => {
            singleValue = removeNodeIdFromThesaurusValue(
              duplicateId,
              singleValue,
            );
          },
        );

        return {
          ...old,
          [thesaurusId]: singleValue,
        };
      });
      multiEditValueTimestampRef.current = timestamp++;
    },
    [],
  );

  const getNodeAction = useCallback(
    (thesaurusId: ThesaurusId, nodeId: string) => {
      return getSelectAction(
        nodeId,
        multiEditValue[thesaurusId],
        thesauruses[thesaurusId]!,
        undefined,
        mapsRef.current[thesaurusId]!.duplicateIdToIds,
      );
    },
    [multiEditValue, thesauruses],
  );

  // When search query is changed
  // => empty query: set empty result
  // => query: update search result
  useEffect(() => {
    if (searchQuery === searchQueryRef.current) {
      return;
    }

    searchQueryRef.current = searchQuery;

    setSearchResult(
      !searchQuery
        ? undefined
        : searchQuery.length < 2
          ? emptySearchResult
          : searchMultiThesaurus(searchQuery, thesaurusValue, thesauruses),
    );
  }, [emptySearchResult, searchQuery, thesaurusValue, thesauruses]);

  // When source value is changed, e.g. other field selected
  // => set edit value accordingly
  useEffect(() => {
    thesaurusValueTimestampRef.current = timestamp++;
    setMultiEditValue(() =>
      mapMultiThesaurusValueToNodeIds(
        thesaurusIds,
        thesaurusValue,
        mapsRef.current,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thesaurusValue]);

  // When edit value is changed,
  // => update source value
  useEffect(() => {
    // Only update source value if edit value is newer
    if (
      multiEditValueTimestampRef.current > thesaurusValueTimestampRef.current
    ) {
      handleSetSourceThesaurusValue(multiEditValue);
    }
  }, [handleSetSourceThesaurusValue, multiEditValue]);

  return useMemo(() => {
    return {
      thesaurusIds,
      showCodes,
      thesauruses,
      searchQuery,
      setSearchQuery,
      searchResult,
      getNodeAction,
      addNodeId: handleAddNodeId,
      removeNodeId: handleRemoveNodeId,
      close: onClose,
    };
  }, [
    getNodeAction,
    handleAddNodeId,
    handleRemoveNodeId,
    onClose,
    searchQuery,
    searchResult,
    showCodes,
    thesaurusIds,
    thesauruses,
  ]);
};

export const useMultiThesaurusEditStateContext =
  (): MultiThesaurusEditStateContextType => {
    return assert(
      useContext(MultiThesaurusEditStateContext),
      'MultiThesaurusEditStateContext: context expected',
    );
  };
